/**
 * DistributionRhythm — 8-quarter distributions timeline card.
 *
 * Phase 3 Task 3.3 (REQ-DASH-003, 2026-05-17): rewired off the Anna
 * QUARTERS + TRUST fixtures onto `useDashboardDistributionsRhythm(trustId)`.
 * Renders one bar per quarter (last 8), with paid/pending status, and a
 * year-to-date roll-up footer derived from the response totals.
 */

import { Link } from '@tanstack/react-router';
import { Icon } from '../icon';
import { mapError } from '../../lib/error-messages';
import {
  formatZarCents,
  useDashboardDistributionsRhythm,
  useDashboardTrustSummary,
  type QuarterPoint,
} from '../../lib/api';

export interface DistributionRhythmProps {
  trustId: string | undefined;
}

function quarterTotalCents(q: QuarterPoint): number {
  return q.paidAmountCents + q.pendingAmountCents;
}

function statusClassName(status: QuarterPoint['status']): string {
  // Map backend status enums onto the legacy CSS classes (signed/pending/future)
  // so the existing dashboard.css styling carries over without churn.
  if (status === 'paid') return 'signed';
  if (status === 'partial') return 'pending';
  if (status === 'pending') return 'pending';
  return 'future';
}

function statusSubtitle(q: QuarterPoint): string {
  if (q.status === 'paid' && q.count > 0) return 'paid';
  if (q.status === 'paid' && q.count === 0) return 'nothing scheduled';
  if (q.status === 'partial') return 'partial - payouts in flight';
  if (q.status === 'pending') return 'awaiting payout';
  return 'projected';
}

export function DistributionRhythm({ trustId }: DistributionRhythmProps) {
  const { data, isLoading, isError, error } =
    useDashboardDistributionsRhythm(trustId);
  // DIST-06: reconcile the pending-payout total against the REAL trust value
  // (the A0 totalValue rollup). `totalValue` is a ZAR major-unit amount;
  // `totalPendingCents` is cents. We never imply a payout can be funded from a
  // zero / unvalued corpus — when the trust is unvalued we show an honest
  // "valuation pending" caveat instead of an affordability claim.
  const summaryQ = useDashboardTrustSummary(trustId);

  if (!trustId) {
    return (
      <div className="distrib">
        <div className="card-head" style={{ marginBottom: 22, alignItems: 'center' }}>
          <div style={{ minWidth: 0 }}>
            <div className="eyebrow">Distribution rhythm</div>
            <h2 className="card-title">Quarterly support</h2>
          </div>
        </div>
        <p style={{ color: 'var(--ink-3)', fontSize: 13, lineHeight: 1.6 }}>
          Distributions appear here once a trust is onboarded. The 8-quarter
          rhythm starts populating after the first trustee resolution
          authorising a payout.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="distrib" aria-busy="true" aria-live="polite">
        <div className="card-head" style={{ marginBottom: 22, alignItems: 'center' }}>
          <div style={{ minWidth: 0 }}>
            <div className="eyebrow">Distribution rhythm</div>
            <h2 className="card-title">Quarterly support</h2>
          </div>
        </div>
        <div
          data-testid="rhythm-skeleton"
          style={{ minHeight: 140, color: 'var(--ink-3)', fontSize: 13, padding: 24 }}
        >
          Loading the last 8 quarters of distributions&hellip;
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="distrib" role="alert">
        <div className="card-head" style={{ marginBottom: 22, alignItems: 'center' }}>
          <div style={{ minWidth: 0 }}>
            <div className="eyebrow">Distribution rhythm</div>
            <h2 className="card-title">Quarterly support</h2>
          </div>
        </div>
        <p style={{ color: 'var(--negative, #b94c50)', fontSize: 13, lineHeight: 1.6 }}>
          Couldn&rsquo;t load the distribution rhythm. Your data is
          safe. {mapError(error, 'load')}
        </p>
      </div>
    );
  }

  const quarters = data?.quarters ?? [];

  if (quarters.length === 0) {
    return (
      <div className="distrib">
        <div className="card-head" style={{ marginBottom: 22, alignItems: 'center' }}>
          <div style={{ minWidth: 0 }}>
            <div className="eyebrow">Distribution rhythm</div>
            <h2 className="card-title">Quarterly support</h2>
          </div>
        </div>
        <p role="status" style={{ color: 'var(--ink-3)', fontSize: 13, lineHeight: 1.6 }}>
          No quarterly distributions on record for this trust yet. The
          first authorised payout marks the start of the rhythm.
        </p>
      </div>
    );
  }

  const totalPaid = data?.totalPaidCents ?? 0;
  const totalPending = data?.totalPendingCents ?? 0;

  // DIST-06: the real corpus value (ZAR major units). `0` means either a
  // genuinely empty trust or one whose nightly valuation hasn't run yet — in
  // both cases we must NOT imply the pending payouts are "affordable".
  const trustValueRand = summaryQ.data?.totalValue ?? null;
  const isUnvalued = trustValueRand === null || trustValueRand <= 0;
  const showAffordabilityCaveat = totalPending > 0 && isUnvalued;

  return (
    <div className="distrib">
      <div className="card-head" style={{ marginBottom: 22, alignItems: 'center' }}>
        <div style={{ minWidth: 0 }}>
          <div className="eyebrow">Distribution rhythm</div>
          <h2 className="card-title">Quarterly support, paid on the dot</h2>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0, whiteSpace: 'nowrap' }}>
          <div className="eyebrow ink" style={{ marginBottom: 4 }}>
            Window
          </div>
          <div
            style={{
              fontFamily: 'var(--serif)',
              fontWeight: 300,
              fontSize: 28,
              lineHeight: 1,
              letterSpacing: '-0.02em',
              color: 'var(--ink)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <span style={{ fontStyle: 'italic', color: 'var(--accent-600)' }}>last</span>{' '}
            {quarters.length}
            <span style={{ fontSize: 15, color: 'var(--ink-3)', fontStyle: 'italic', marginLeft: 6 }}>
              quarters
            </span>
          </div>
        </div>
      </div>

      <div className="quarters">
        {quarters.map((q) => {
          // A `paid` quarter with nothing actually scheduled (count 0, R0) is
          // NOT a success — a green tick + green "signed" fill on an empty R0
          // quarter reads as "we paid out" when nothing happened. Render it as a
          // neutral quarter instead (no green, no check), so the rhythm only
          // celebrates quarters that genuinely paid.
          const isEmptyPaid = q.status === 'paid' && q.count === 0;
          const cls = isEmptyPaid ? 'future' : statusClassName(q.status);
          return (
            <div key={q.label} className={'quarter ' + cls} data-status={q.status}>
              <div className="quarter-tick">
                {q.status === 'paid' && !isEmptyPaid && (
                  <Icon name="check" size={10} stroke={3} color="white" />
                )}
                {(q.status === 'pending' || q.status === 'partial') && (
                  <Icon name="clock" size={10} stroke={2} color="var(--primary-800)" />
                )}
                {q.status === 'future' && (
                  <Icon name="clock" size={10} stroke={2} color="var(--bg)" />
                )}
              </div>
              <div className="quarter-label">{q.label}</div>
              <div className="quarter-amt num">{formatZarCents(quarterTotalCents(q))}</div>
              <div className="quarter-sub">{statusSubtitle(q)}</div>
            </div>
          );
        })}
      </div>

      <div
        style={{
          marginTop: 22,
          paddingTop: 18,
          borderTop: '1px solid var(--hairline)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 13.5,
          color: 'var(--ink-2)',
        }}
      >
        <div>
          <span style={{ color: 'var(--ink-3)' }}>Paid in window </span>
          <span className="num" style={{ fontWeight: 500, color: 'var(--ink)' }}>
            {formatZarCents(totalPaid)}
          </span>
          {totalPending > 0 && (
            <>
              <span style={{ color: 'var(--ink-3)', marginLeft: 6 }}>-</span>
              <span style={{ color: 'var(--ink-3)', marginLeft: 6 }}>pending </span>
              <span className="num" style={{ fontWeight: 500, color: 'var(--ink)' }}>
                {formatZarCents(totalPending)}
              </span>
            </>
          )}
        </div>
        {/* DIST-04 / TRUSTEE-07: real navigation to the trust's distribution
            history (was a dead href="#"). Hidden when there's no trust in
            scope — there's nowhere to link to. */}
        {trustId ? (
          <Link
            to="/app/trusts/$id/distributions"
            params={{ id: trustId }}
            className="card-link"
            data-testid="distribution-history-link"
          >
            Distribution history &rarr;
          </Link>
        ) : null}
      </div>

      {/* DIST-06: honest affordability caveat. Shown only when there ARE
          pending payouts AND the trust corpus is zero/unvalued — so the
          pending figure is never read as "the trust can fund this". */}
      {showAffordabilityCaveat && (
        <p
          data-testid="affordability-caveat"
          style={{
            marginTop: 12,
            fontSize: 12,
            color: 'var(--ink-3)',
            lineHeight: 1.5,
          }}
        >
          Trust valuation pending: the pending figure is the total of scheduled
          payouts, not a confirmation that the trust can fund them. Affordability
          is confirmed once the portfolio valuation is recorded.
        </p>
      )}
    </div>
  );
}
